import { API, graphqlOperation } from 'aws-amplify';
import { variables as fallbackVariables } from './subscriptions';

const IOT_TYPE_NAME = 'IOT';
const SCALAR_TYPE_KINDS = new Set(['SCALAR', 'ENUM']);
const SCHEMA_CACHE_TTL_MS = 60 * 1000;

const introspectionQuery = /* GraphQL */ `
  query IntrospectIOTFields {
    __type(name: "IOT") {
      fields {
        name
        type {
          kind
          name
          ofType {
            kind
            name
            ofType {
              kind
              name
              ofType {
                kind
                name
              }
            }
          }
        }
      }
    }
  }
`;

let fieldsPromise = null;
let cachedFields = null;
let cachedAt = 0;

const splitFieldList = (fieldList) =>
  String(fieldList || '')
    .split(/\s+/)
    .map((field) => field.trim())
    .filter(Boolean);

const fallbackFields = splitFieldList(fallbackVariables);

const getNamedType = (type) => {
  let currentType = type;
  while (currentType && currentType.ofType) {
    currentType = currentType.ofType;
  }
  return currentType;
};

const responseData = (response) => response?.data || response || {};

const extractIotFields = (response) => {
  // eslint-disable-next-line no-underscore-dangle
  const fields = responseData(response).__type?.fields || [];
  return fields
    .filter((field) => SCALAR_TYPE_KINDS.has(getNamedType(field.type)?.kind))
    .map((field) => field.name)
    .filter(Boolean);
};

const loadIotFields = async ({ force = false } = {}) => {
  if (!force && cachedFields && Date.now() - cachedAt < SCHEMA_CACHE_TTL_MS) {
    return cachedFields;
  }
  if (!force && fieldsPromise) return fieldsPromise;

  fieldsPromise = API.graphql(graphqlOperation(introspectionQuery))
    .then((response) => {
      const fields = extractIotFields(response);
      cachedFields = fields.length ? fields : fallbackFields;
      cachedAt = Date.now();
      return cachedFields;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn(
        `[graphql:${IOT_TYPE_NAME}] introspection falhou; usando fallback estatico`,
        error
      );
      cachedFields = fallbackFields;
      cachedAt = Date.now();
      return cachedFields;
    })
    .finally(() => {
      fieldsPromise = null;
    });

  return fieldsPromise;
};

const buildSelection = (fields) => fields.join('\n      ');

export const resetIotGraphqlDocuments = () => {
  cachedFields = null;
  fieldsPromise = null;
  cachedAt = 0;
};

export const refreshIotGraphqlDocuments = async () => {
  resetIotGraphqlDocuments();
  return loadIotFields({ force: true });
};

export const getIOTDocument = async () => {
  const fields = await loadIotFields();
  return /* GraphQL */ `
    query GetIOT($thingName: String!) {
      getIOT(thingName: $thingName) {
        ${buildSelection(fields)}
      }
    }
  `;
};

export const listIOTSDocument = async () => {
  const fields = await loadIotFields();
  return /* GraphQL */ `
    query ListIOTS(
      $filter: TableIOTFilterInput
      $limit: Int
      $nextToken: String
    ) {
      listIOTS(filter: $filter, limit: $limit, nextToken: $nextToken) {
        items {
          ${buildSelection(fields)}
        }
        nextToken
      }
    }
  `;
};

export const onUpdateIOTDocument = async () => {
  const fields = await loadIotFields();
  return /* GraphQL */ `
    subscription OnUpdateIOT($thingName: String!) {
      onUpdateIOT(thingName: $thingName) {
        ${buildSelection(fields)}
      }
    }
  `;
};
